/**
 * The student's own result, read-only.
 *
 * `GET /api/attempts/:id/result-detail` is student-scoped and ownership-gated
 * on the server — a student only ever gets their OWN attempt back. The reveal
 * fields (correct answer, solution, correctness) are present only when the exam
 * allows it (`reveal_answers`); the performance aggregates always come back.
 *
 * The fetch is a plain, honest call: on any failure the page shows a friendly
 * "not ready yet" state rather than fabricated numbers — never mask a real
 * server error with sample data on a student's own result. `SAMPLE_RESULT` is a
 * layout-preview aid only, reached through an explicit `?sample=1` on the page.
 */
import { ResultDetailResponse } from "@exam/shared";
import type { ResultDetailResponse as ResultDetail } from "@exam/shared";
import { api } from "@/lib/api";

export async function fetchResultDetail(
  attemptId: string,
): Promise<ResultDetail> {
  const raw = await api.get<unknown>(
    `/api/attempts/${attemptId}/result-detail`,
  );
  return ResultDetailResponse.parse(raw);
}

/* ------------------------------------------------------------------ */
/* Sample — mirrors a real multi-subject attempt so the layout is honest */
/* ------------------------------------------------------------------ */

export const SAMPLE_RESULT: ResultDetail = {
  overall: { score: 25, max_score: 112, percent: 22.3, correct: 14, incorrect: 30, unanswered: 12 },
  by_subject: [
    { subject_id: "sub-math", subject_name: "Mathematics", correct: 4, answered: 20, marks_awarded: 6, max_marks: 56 },
    { subject_id: "sub-phys", subject_name: "Physics", correct: 10, answered: 24, marks_awarded: 19, max_marks: 56 },
  ],
  by_type: [
    { type: "MCQ", correct: 13, answered: 21, },
    { type: "MSQ", correct: 0, answered: 9 },
    { type: "NAT", correct: 1, answered: 12 },
  ],
  by_topic: [
    { topic_id: "t-sets", topic_name: "Sets & Relations", subject_name: "Mathematics", week_no: 1, correct: 0, answered: 4 },
    { topic_id: "t-msq", topic_name: "Rotational Motion", subject_name: "Physics", week_no: 3, correct: 1, answered: 6 },
    { topic_id: "t-calc", topic_name: "Differential Calculus", subject_name: "Mathematics", week_no: 2, correct: 2, answered: 7 },
    { topic_id: "t-kin", topic_name: "Kinematics", subject_name: "Physics", week_no: 1, correct: 6, answered: 9 },
  ],
  reveal_answers: true,
  questions: [
    {
      order_idx: 0,
      subject_id: "sub-math",
      subject_name: "Mathematics",
      topic_name: "Sets & Relations",
      type: "MCQ",
      body_md: "If $A = \\{1,2,3\\}$ and $B = \\{2,3,4\\}$, then $A \\cap B$ is:",
      options: [
        { key: "A", text: "$\\{1,2,3,4\\}$" },
        { key: "B", text: "$\\{2,3\\}$" },
        { key: "C", text: "$\\{1\\}$" },
        { key: "D", text: "$\\{4\\}$" },
      ],
      response: { type: "MCQ", key: "A" },
      marks: 4,
      marks_awarded: -1,
      correct_answer: { type: "MCQ", key: "B" },
      is_correct: false,
      solution_md: "The intersection keeps only elements in **both** sets: $\\{2,3\\}$.",
    },
    {
      order_idx: 1,
      subject_id: "sub-phys",
      subject_name: "Physics",
      topic_name: "Kinematics",
      type: "NAT",
      body_md: "A body starts from rest with acceleration $2\\,\\text{m/s}^2$. Its speed after $3$ s (in m/s)?",
      options: [],
      response: { type: "NAT", value: 6 },
      marks: 4,
      marks_awarded: 4,
      correct_answer: { type: "NAT", value: 6, tolerance: 0 },
      is_correct: true,
      solution_md: "$v = u + at = 0 + 2\\times 3 = 6\\,\\text{m/s}$.",
    },
  ],
};
